import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

const BackgroundMusic = ({ music, volume = 0.5, fade = false }) => {
  const [audioContext, setAudioContext] = useState(null);
  const [audioBuffer, setAudioBuffer] = useState(null);
  const [sourceNode, setSourceNode] = useState(null);
  const [gainNode, setGainNode] = useState(null);

  const playAudio = () => {
    if (sourceNode) {
      sourceNode.stop();
    }

    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.loop = true;

    const gain = audioContext.createGain();
    gain.gain.value = volume;
    source.connect(gain);
    gain.connect(audioContext.destination);

    source.start(0);
    setSourceNode(source);
    setGainNode(gain);
  };

  const resumeAudio = () => audioContext.resume();

  useEffect(() => {
    const context = new AudioContext();
    setAudioContext(context);

    return () => {
      if (context) {
        context.close();
        window.removeEventListener('click', resumeAudio);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!audioContext || !music) return;

    try {
      fetch(music).then((response) => {
        response.arrayBuffer().then((arrayBuffer) => {
          audioContext
            .decodeAudioData(arrayBuffer)
            .then((buffer) => setAudioBuffer(buffer));
        });
      });
    } catch (error) {
      console.warn('Error loading audio:', error);
    }
  }, [audioContext, music]);

  useEffect(() => {
    if (!audioContext || !audioBuffer) return;
    // try to play the audio when the component first mounts
    playAudio();
    // try to resume the audio when the user clicks
    window.addEventListener('click', resumeAudio);
    return () => {
      window.removeEventListener('click', resumeAudio);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioContext, audioBuffer]);

  useEffect(() => {
    if (fade) {
      const interval = setInterval(() => {
        if (sourceNode && fade) {
          sourceNode.detune.value += -50;
          sourceNode.playbackRate.value += -0.05;
          if (gainNode) {
            gainNode.gain.value = gainNode.gain.value * 0.9;
          }
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [sourceNode, gainNode, fade]);

  useEffect(() => {
    if (!fade && gainNode) {
      gainNode.gain.value = volume;
    }
  }, [gainNode, fade, volume]);
};

BackgroundMusic.propTypes = {
  music: PropTypes.string.isRequired,
  volume: PropTypes.number,
  fade: PropTypes.bool
};

export default BackgroundMusic;
